import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, SlidersHorizontal } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { addDoc, collection, deleteDoc, deleteField, doc, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { useNavbar } from "./DashboardLayout";
import PrepSetup from "../components/prep/PrepSetup";
import PrepSettings, { type SettingsPatch } from "../components/prep/PrepSettings";
import PrepWeek, { LEGEND, type ItemActions } from "../components/prep/PrepWeek";
import PrepItemPopover, { OpenLink, shownReason, type DebriefProps } from "../components/prep/PrepItem";
import PrepGoals from "../components/prep/PrepGoals";
import PrepOaCard, { OA_COLOR } from "../components/prep/PrepOaCard";
import { detectOaKind, type PrepEventDoc } from "../utils/prepEvents";
import { shareDebrief } from "../utils/companyDebriefs";
import AddCareerGoal from "../components/addCareerGoal";
import { ROLE_GROUPS } from "../components/onboarding/fields";
import { goalItemId } from "../utils/prepPlanner";
import type { CareerGoal } from "../utils/goals";
import { addDays, daysBetween, weekday } from "../utils/prepSkills";
import { HORIZON, localToday, plain, usePrepPlan } from "../utils/usePrepPlan";
import {
  DEFAULT_DAY_MINUTES,
  defaultLevel,
  formatMinutes,
  levelSuggestion,
  streak,
  timeSummary,
  weekLabel,
  weekOf,
  type PrepSettingsDoc,
} from "../utils/prep";
import { cn } from "../lib/utils";

// Interview Prep: a day-by-day plan from the user's time, weak topics and
// pace (utils/prepPlanner.ts). Setup comes first; after that the page is the
// week calendar, today's items, the week's progress and the user's own goals
// (their steps are planned in too, in blue). See docs/plans/INTERVIEW_PREP_V1.md.

const CARD = "rounded-2xl border border-melt-line bg-melt-paper p-4";
const LEVEL_NAME = { intern: "internship", newgrad: "new grad", mid: "mid-level", senior: "senior" } as const;

export default function InterviewPrep() {
  const { setNavbarActions, userRecord } = useNavbar();
  const [user, setUser] = useState(auth.currentUser);
  const [authReady, setAuthReady] = useState(auth.currentUser !== null);
  const [goalModal, setGoalModal] = useState<{ edit?: CareerGoal } | null>(null);
  const [editing, setEditing] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { today, loaded, settings, problems, states, stateById, goals, itemsOn, plannedDay, events, skills, cards, eventDocs, crowds } = usePrepPlan(user);
  const [weekStart, setWeekStart] = useState(() => weekOf(localToday())[0]);

  useEffect(
    () =>
      onAuthStateChanged(auth, (currentUser) => {
        setUser(currentUser);
        setAuthReady(true);
      }),
    []
  );

  // ---------------------------------------------------------------- writes

  const itemRef = (id: string) => doc(db, "users", user!.uid, "prepItems", id);
  const fail = (what: string) => (e: unknown) => {
    console.error(`Could not ${what}:`, e);
    alert(`Couldn’t ${what}. Try again.`);
  };

  /** Check a goal step off (or back on) on the goal itself */
  const setStep = async (goalId: string, stepId: string, completed: boolean) => {
    const goal = goals.find((g) => g.id === goalId);
    if (!user || !goal) return;
    await updateDoc(doc(db, "users", user.uid, "careerGoals", goalId), {
      lessons: (goal.lessons || []).map((step) => (step.id === stepId ? { ...step, completed } : step)),
    });
  };

  /** A step unchecked on its goal: its calendar item isn't done anymore either */
  const clearDone = async (id: string) => {
    const saved = states.find((s) => s.item.id === id);
    if (!saved?.status) return;
    if (saved.movedTo) await updateDoc(itemRef(id), { status: deleteField() });
    else await deleteDoc(itemRef(id));
  };

  const toggleStep = (goal: CareerGoal, stepId: string, completed: boolean) =>
    setStep(goal.id, stepId, completed)
      .then(() => (completed ? undefined : clearDone(goalItemId(goal.id, stepId))))
      .catch(fail("update that step"));

  const deleteGoal = async (goal: CareerGoal) => {
    if (!user) return;
    try {
      await deleteDoc(doc(db, "users", user.uid, "careerGoals", goal.id));
      await Promise.all(states.filter((s) => s.item.goalId === goal.id).map((s) => deleteDoc(itemRef(s.item.id))));
    } catch (e) {
      fail("delete that goal")(e);
    }
  };

  const actions: ItemActions = {
    onDone: async (item, day, result, minutes) => {
      if (!user) return;
      try {
        if (item.kind === "goal" && item.goalId && item.stepId) await setStep(item.goalId, item.stepId, true);
        if (item.kind === "debrief" && item.eventId) await setEventDoc(item.eventId, { debriefed: true });
        let resultId: string | undefined;
        if (item.kind === "problem" && result && item.slug && item.topic && item.difficulty) {
          const ref = await addDoc(collection(db, "users", user.uid, "prepResults"), {
            slug: item.slug,
            topic: item.topic,
            difficulty: item.difficulty,
            result,
            ...(minutes ? { minutes } : {}),
            day: today,
            at: serverTimestamp(),
          });
          resultId = ref.id;
        }
        await setDoc(
          itemRef(item.id),
          plain({ item, day: stateById.get(item.id)?.day ?? day, status: "done", result, minutes, resultId }),
          { merge: true }
        );
      } catch (e) {
        fail("save that")(e);
      }
    },
    onSkip: (item, day, back) => {
      if (!user) return;
      const status = back ? "later" : "skipped";
      setDoc(itemRef(item.id), plain({ item, day: stateById.get(item.id)?.day ?? day, status }), { merge: true }).catch(
        fail("skip that")
      );
    },
    onUndo: async (item) => {
      const state = stateById.get(item.id);
      if (!user || !state) return;
      try {
        if (item.kind === "goal" && item.goalId && item.stepId) await setStep(item.goalId, item.stepId, false);
        if (item.kind === "debrief" && item.eventId) {
          await setDoc(doc(db, "users", user.uid, "prepEvents", item.eventId), { debriefed: false, debrief: deleteField() }, { merge: true });
        }
        if (state.resultId) await deleteDoc(doc(db, "users", user.uid, "prepResults", state.resultId));
        if (state.movedTo) {
          await updateDoc(itemRef(item.id), {
            status: deleteField(),
            result: deleteField(),
            minutes: deleteField(),
            resultId: deleteField(),
          });
        } else {
          await deleteDoc(itemRef(item.id));
        }
      } catch (e) {
        fail("undo that")(e);
      }
    },
    onMove: (item, from, to) => {
      if (!user) return;
      const state = stateById.get(item.id);
      const original = state?.day ?? from;
      const write =
        to === original
          ? state?.status
            ? updateDoc(itemRef(item.id), { movedTo: deleteField() })
            : deleteDoc(itemRef(item.id))
          : setDoc(itemRef(item.id), plain({ item, day: original, movedTo: to }), { merge: true });
      write.catch(fail("move that"));
    },
    debriefFor: (item) => (item.kind === "debrief" && item.eventId ? debriefProps(item.eventId) : undefined),
  };

  /**
   * The debrief for an OA, from its calendar item, Today or the OA card.
   * Saving it also checks off its debrief item wherever that's planned.
   */
  const debriefProps = (eventId: string): DebriefProps => {
    const card = cards.find((c) => c.id === eventId);
    const event = events.find((e) => e.id === eventId);
    return {
      company: event?.company ?? card?.company ?? "your",
      kind: eventDocs[eventId]?.kind ?? (card ? detectOaKind(card) : undefined),
      initial: eventDocs[eventId]?.debrief,
      today,
      onSave: (debrief) => {
        const item = itemsOn(today).find((i) => i.kind === "debrief" && i.eventId === eventId);
        const where = plain({ company: card?.company || undefined, role: card?.role || undefined });
        const kind = eventDocs[eventId]?.kind ?? (card ? detectOaKind(card) : undefined);
        setEventDoc(eventId, { debrief: { ...debrief, ...where }, debriefed: true })
          .then(() => item && actions.onDone(item, today))
          .catch(fail("save the debrief"));
        // Shared (or unshared) on its own, so a missing Firestore rule can't stop the debrief saving
        shareDebrief(user!.uid, card, debrief, kind).catch((e) => console.warn("Could not share the debrief:", e));
      },
    };
  };

  const saveSettings = async (answers: Omit<PrepSettingsDoc, "completedAt">, selfPrep: string[] = []) => {
    if (!user) return;
    try {
      await Promise.all(selfPrep.map((id) => setEventDoc(id, { selfPrep: true })));
      await setDoc(doc(db, "users", user.uid, "prep", "settings"), {
        ...answers,
        completedAt: settings?.completedAt ?? today,
      });
      setEditing(false);
    } catch (e) {
      fail("save your setup")(e);
    }
  };

  /** What the user said about an OA (users/{uid}/prepEvents/{cardId}) */
  const setEventDoc = (cardId: string, patch: PrepEventDoc) =>
    setDoc(doc(db, "users", user!.uid, "prepEvents", cardId), patch, { merge: true });

  /** From the settings page: each change saves as it's made */
  const updateSettings = (patch: SettingsPatch) => {
    if (!user) return;
    setDoc(doc(db, "users", user.uid, "prep", "settings"), patch, { merge: true }).catch(fail("save that"));
  };

  // ---------------------------------------------------------------- header

  const ready = Boolean(settings) && !editing;
  useEffect(() => {
    setNavbarActions(
      <div className="flex w-full min-w-0 items-center justify-between gap-4 font-grotesk">
        <h1 className="whitespace-nowrap text-[22px] font-semibold leading-none tracking-[-.03em] text-melt-text1">
          {editing && settings ? "Prep settings" : "Interview Prep"}
        </h1>
        {ready && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            aria-label="Prep settings"
            title="Your time and topics"
            className="grid h-8 w-8 place-items-center rounded-[9px] text-melt-text3 transition-colors hover:bg-melt-text1/[.07] hover:text-melt-text1"
          >
            <SlidersHorizontal size={17} strokeWidth={1.75} />
          </button>
        )}
      </div>
    );
  }, [setNavbarActions, ready, editing, settings]);
  useEffect(() => () => setNavbarActions(null), [setNavbarActions]);

  // ---------------------------------------------------------------- render

  if (!authReady || (user && !loaded) || (settings && !problems)) {
    return <div className="flex items-center justify-center py-12 font-grotesk text-melt-text3">Loading…</div>;
  }

  if (settings && editing) {
    return (
      <PrepSettings
        settings={settings}
        skills={skills!}
        onChange={updateSettings}
        onBack={() => setEditing(false)}
      />
    );
  }

  // Dev only: /dashboard/prep?preview=setup shows setup to an account that's
  // done it, without saving
  const previewSetup = process.env.NODE_ENV !== "production" && searchParams.get("preview") === "setup";

  if (!settings || previewSetup) {
    const roles = Array.isArray(userRecord?.roles) ? (userRecord!.roles as string[]) : [];
    return (
      <PrepSetup
        initial={
          {
            dayMinutes: { ...DEFAULT_DAY_MINUTES },
            level: defaultLevel(userRecord?.jobTypes),
            leetcodePremium: false,
            ratings: {},
          }
        }
        softwareRoles={!roles.length || roles.some((r) => ROLE_GROUPS.Software.includes(r))}
        // Only OAs still ahead: one that's over just waits for its debrief
        oas={events.filter((e) => !e.takenOn)}
        onSave={previewSetup ? async () => setSearchParams({}) : saveSettings}
      />
    );
  }

  const days = weekOf(weekStart);
  const firstWeek = weekOf(settings.completedAt)[0];
  const lastWeek = weekOf(addDays(today, HORIZON - 1))[0];
  const todayList = itemsOn(today);
  const todayMinutes = todayList.reduce((sum, i) => sum + i.minutes, 0);
  const hideTopics = Boolean(settings.hideTopics);
  const reasons = Array.from(
    new Set(todayList.filter((i) => !stateById.get(i.id)?.status).map((i) => shownReason(i, hideTopics)).filter(Boolean))
  ).slice(0, 2);

  const thisWeek = weekOf(today).flatMap((day) => itemsOn(day));
  const planned = thisWeek.reduce((sum, i) => sum + i.minutes, 0);
  const done = thisWeek.filter((i) => stateById.get(i.id)?.status === "done").reduce((sum, i) => sum + i.minutes, 0);
  const shownDays = Object.fromEntries(
    Array.from({ length: daysBetween(settings.completedAt, today) + 1 }, (_, i) => {
      const day = addDays(settings.completedAt, i);
      return [day, itemsOn(day)];
    })
  );
  const streakDays = streak(shownDays, stateById, today, settings.completedAt);

  // Job search settings changed since setup (Internship -> New grad, say): ask, don't reshuffle the plan
  const suggested = levelSuggestion(userRecord?.jobTypes, settings);

  const arrow =
    "grid h-7 w-7 place-items-center rounded-[8px] text-melt-text3 transition-colors hover:bg-melt-text1/[.07] hover:text-melt-text1 disabled:opacity-30 disabled:hover:bg-transparent";

  return (
    <div className="flex w-full flex-col gap-6 pb-8 font-grotesk text-melt-text1 xl:flex-row xl:items-start">
      <section className="flex min-w-0 flex-1 flex-col gap-4">
        {suggested && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl bg-melt-peach px-3.5 py-2.5 text-[13px] text-melt-text2">
            <p className="min-w-0 flex-1">
              Your job search is set to {LEVEL_NAME[suggested]} roles. Prep for {LEVEL_NAME[suggested]} interviews instead?
            </p>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => updateSettings({ level: suggested })}
                className="rounded-lg px-2 py-1 font-medium text-melt-caramel transition-colors hover:bg-melt-text1/[.05]"
              >
                Switch
              </button>
              <button
                type="button"
                onClick={() => updateSettings({ declinedLevel: suggested })}
                className="rounded-lg px-2 py-1 text-melt-text3 transition-colors hover:bg-melt-text1/[.05] hover:text-melt-text1"
              >
                Keep {LEVEL_NAME[settings.level]}
              </button>
            </div>
          </div>
        )}
        {events.map((event) => (
          <PrepOaCard
            key={event.id}
            event={event}
            today={today}
            debrief={debriefProps(event.id)}
            crowd={crowds.get(event.id)}
            hideTopics={hideTopics}
            onSelfPrep={() => setEventDoc(event.id, { selfPrep: true }).catch(fail("save that"))}
          />
        ))}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-[15px] font-medium">{timeSummary(settings.dayMinutes)}.</p>
          <div className="flex items-center gap-1 rounded-[10px] bg-white/70 px-1 py-0.5">
            <button type="button" className={arrow} aria-label="Previous week" disabled={weekStart <= firstWeek} onClick={() => setWeekStart(addDays(weekStart, -7))}>
              <ChevronLeft size={16} strokeWidth={1.75} />
            </button>
            <span className="min-w-[96px] text-center font-plex text-xs text-melt-text1">{weekLabel(days)}</span>
            <button type="button" className={arrow} aria-label="Next week" disabled={weekStart >= lastWeek} onClick={() => setWeekStart(addDays(weekStart, 7))}>
              <ChevronRight size={16} strokeWidth={1.75} />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {[...LEGEND, ...(events.length ? [{ label: "OA prep", style: OA_COLOR.block }] : [])].map(({ label, style }) => (
            <span key={label} className="flex items-center gap-1.5 font-plex text-[11px] text-melt-text3">
              <i className={cn("h-2.5 w-2.5 rounded-[3px]", style)} />
              {label}
            </span>
          ))}
        </div>

        <PrepWeek
          days={days}
          hideTopics={hideTopics}
          today={today}
          itemsOn={itemsOn}
          minutesOn={(day) => settings.dayMinutes[weekday(day)]}
          states={stateById}
          actions={actions}
        />
        <p className="font-plex text-[11px] text-melt-text3">
          Won’t get to something? Drag it to another day.
        </p>
      </section>

      <aside className="flex w-full shrink-0 flex-col gap-3 xl:w-[300px]">
        <div className={CARD}>
          <h2 className="text-[15px] font-semibold">
            Today{todayMinutes ? ` · ${formatMinutes(todayMinutes)}` : ""}
          </h2>
          {todayList.length === 0 ? (
            <p className="mt-2 text-[13px] text-melt-text2">Rest day. Nothing planned.</p>
          ) : (
            <ul className="mt-3 flex flex-col gap-2.5">
              {todayList.map((item) => {
                const state = stateById.get(item.id);
                const finished = Boolean(state?.status);
                return (
                  <li key={item.id} className="flex items-start gap-2.5">
                    <PrepItemPopover
                      item={item}
                      state={state}
                      align="end"
                      onDone={(result, minutes) => actions.onDone(item, today, result, minutes)}
                      onSkip={(back) => actions.onSkip(item, today, back)}
                      onUndo={() => actions.onUndo(item)}
                      debrief={actions.debriefFor(item, today)}
                      hideTopics={hideTopics}
                    >
                      <button
                        type="button"
                        aria-label={finished ? `${item.title}: done` : `Check off ${item.title}`}
                        className={cn(
                          "mt-0.5 grid h-[18px] w-[18px] shrink-0 place-items-center rounded-[5px] border transition-colors",
                          finished ? "border-melt-caramel bg-melt-caramel text-white" : "border-melt-text3/50 bg-white hover:border-melt-caramel"
                        )}
                      >
                        {finished && (
                          <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                            <path d="M2.5 6.2 5 8.5l4.5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </button>
                    </PrepItemPopover>
                    <div className="min-w-0">
                      <p className={cn("text-[13.5px] leading-snug", finished ? "text-melt-text3" : "text-melt-text1", (state?.status === "skipped" || state?.status === "later") && "line-through")}>
                        {item.title}
                      </p>
                      {!finished && <OpenLink item={item} className="font-plex text-[11px] text-melt-text3" />}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {reasons.length > 0 && (
            <p className="mt-3 rounded-xl bg-melt-peach px-3 py-2.5 text-[12.5px] leading-[1.5] text-melt-text2">
              <b className="font-semibold text-melt-text1">Why these: </b>
              {reasons.join(". ")}.
            </p>
          )}
        </div>

        <div className={CARD}>
          <h2 className="text-[15px] font-semibold">This week</h2>
          <div className="mt-2 flex items-baseline justify-between text-[13px] text-melt-text2">
            <span>Done</span>
            <span className="font-plex text-xs">
              {formatMinutes(done)} / {formatMinutes(planned)}
            </span>
          </div>
          <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-melt-track">
            <div className="h-full rounded-full bg-melt-caramel transition-[width] duration-500" style={{ width: `${planned ? Math.min(100, (done / planned) * 100) : 0}%` }} />
          </div>
          <p className="mt-2.5 text-[12.5px] leading-[1.5] text-melt-text2">
            {streakDays > 0
              ? `${streakDays} day streak. Finish every item on a day to keep it going.`
              : "Finish every item on a day to start a streak."}
          </p>
        </div>

        <PrepGoals
          className={CARD}
          goals={goals}
          today={today}
          plannedOn={(goalId, stepId) => plannedDay.get(goalItemId(goalId, stepId))}
          onAdd={() => setGoalModal({})}
          onEdit={(goal) => setGoalModal({ edit: goal })}
          onDelete={deleteGoal}
          onToggleStep={toggleStep}
        />
      </aside>

      <AddCareerGoal isOpen={goalModal !== null} onClose={() => setGoalModal(null)} editGoal={goalModal?.edit} />
    </div>
  );
}
